<template>
  <Sidebar
    @open-create-modal="openCreateModal"
    @logout="logout"
  />

  <div class="lg:ml-64 p-4 md:p-8 w-full min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 mb-2">Payment Settlement</h1>
            <p class="text-gray-600">Process approved requests awaiting settlement</p>
          </div>
          <div v-if="pendingSettlementRequests.length > 0" class="flex gap-3">
            <button
              v-if="selectedRequests.length === 0"
              @click="selectAll"
              class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Select All
            </button>
            <button
              v-if="selectedRequests.length > 0"
              @click="clearSelection"
              class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Selection ({{ selectedRequests.length }})
            </button>
            <button
              v-if="selectedRequests.length > 0"
              @click="openBulkSettlementModal"
              class="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Settle Selected ({{ selectedRequests.length }})
            </button>
          </div>
        </div>
      </div>

      <!-- Pending Settlement Requests -->
      <div class="grid gap-6">
        <div v-if="loading" class="text-center py-8">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
          <p class="text-gray-600 mt-4">Loading requests...</p>
        </div>

        <div v-else-if="pendingSettlementRequests.length === 0" class="bg-white rounded-lg p-8 text-center border border-gray-200">
          <p class="text-gray-600 text-lg">No requests pending settlement</p>
        </div>

        <div v-else class="space-y-4">
          <div
            v-for="request in pendingSettlementRequests"
            :key="request._id"
            :class="[
              'bg-white rounded-lg p-6 transition-colors border-2',
              isSelected(request._id) ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'
            ]"
          >
            <!-- Request Header -->
            <div class="flex justify-between items-start mb-4">
              <!-- Checkbox for selection -->
              <div class="flex items-start gap-4 flex-1">
                <input
                  type="checkbox"
                  :checked="isSelected(request._id)"
                  @change="toggleSelection(request._id)"
                  class="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                />
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 mb-1">
                    RF{{ request.amount.toLocaleString() }}
                  </h3>
                  <p class="text-sm text-gray-600">
                    {{ request.requester ? request.requester.name : 'N/A' }} |
                    {{ request.department }}
                  </p>
                </div>
              </div>
              <span
                :class="{
                  'bg-yellow-100 text-yellow-700': request.priority === 'High',
                  'bg-blue-100 text-blue-700': request.priority === 'Medium',
                  'bg-gray-100 text-gray-700': request.priority === 'Low'
                }"
                class="px-3 py-1 rounded-full text-sm font-medium"
              >
                {{ request.priority }} Priority
              </span>
            </div>

            <!-- Request Details Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p class="text-gray-600 text-sm">Purpose</p>
                <p class="text-gray-900">{{ request.purpose }}</p>
              </div>
              <div>
                <p class="text-gray-600 text-sm">Payment Method</p>
                <p class="text-gray-900">{{ request.paymentMethod }}</p>
              </div>
              <div>
                <p class="text-gray-600 text-sm">Preferred Date</p>
                <p class="text-gray-900">{{ new Date(request.preferredPaymentDate).toLocaleDateString() }}</p>
              </div>
              <div>
                <p class="text-gray-600 text-sm">Beneficiary</p>
                <p class="text-gray-900">{{ request.supplier || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-600 text-sm">Beneficiary Account/MoMo</p>
                <p class="text-gray-900">{{ request.beneficiaryAccount || 'N/A' }}</p>
              </div>
            </div>

            <!-- Approval Path -->
            <div class="mb-4">
              <p class="text-gray-600 text-sm mb-2">Approval Trail</p>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(approval, index) in request.approvalPath"
                  :key="index"
                  class="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                >
                  {{ formatRole(approval.role) }}: {{ approval.approver ? approval.approver.name : 'N/A' }} ✓
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex gap-3">
              <button
                @click="openSettlementModal(request)"
                class="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Process Settlement
              </button>
              <button
                @click="viewRequestDetails(request)"
                class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Request Details Modal -->
    <div
      v-if="showDetailsModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeDetailsModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-gray-900">Request Details</h2>
          <button @click="closeDetailsModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div v-if="detailsRequest" class="space-y-6">
          <!-- Request Summary -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm text-gray-600">Request ID</p>
                <p class="text-lg font-mono font-semibold text-gray-900">#{{ detailsRequest._id.slice(-8).toUpperCase() }}</p>
              </div>
              <span
                :class="{
                  'bg-yellow-100 text-yellow-700': detailsRequest.priority === 'High',
                  'bg-blue-100 text-blue-700': detailsRequest.priority === 'Medium',
                  'bg-gray-100 text-gray-700': detailsRequest.priority === 'Low'
                }"
                class="px-3 py-1 rounded-full text-sm font-medium"
              >
                {{ detailsRequest.priority }} Priority
              </span>
            </div>
            <div class="mt-4">
              <p class="text-sm text-gray-600">Amount</p>
              <p class="text-3xl font-bold text-gray-900">RF{{ detailsRequest.amount.toLocaleString() }}</p>
            </div>
          </div>

          <!-- Requester Info -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600">Requester</p>
              <p class="font-medium text-gray-900">{{ detailsRequest.requester ? detailsRequest.requester.name : 'N/A' }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Department</p>
              <p class="font-medium text-gray-900">{{ detailsRequest.department }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Beneficiary</p>
              <p class="font-medium text-gray-900">{{ detailsRequest.supplier || 'N/A' }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Beneficiary Account/MoMo</p>
              <p class="font-medium text-gray-900">{{ detailsRequest.beneficiaryAccount || 'N/A' }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Payment Method</p>
              <p class="font-medium text-gray-900">{{ detailsRequest.paymentMethod }}</p>
            </div>
          </div>

          <!-- Purpose -->
          <div>
            <p class="text-sm text-gray-600 mb-1">Purpose</p>
            <p class="text-gray-900 bg-gray-50 p-3 rounded-lg border border-gray-200">{{ detailsRequest.purpose }}</p>
          </div>

          <!-- Dates -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600">Preferred Payment Date</p>
              <p class="font-medium text-gray-900">{{ formatDateTime(detailsRequest.preferredPaymentDate) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Request Created</p>
              <p class="font-medium text-gray-900">{{ formatDateTime(detailsRequest.createdAt) }}</p>
            </div>
          </div>

          <!-- Approval Path -->
          <div>
            <p class="text-sm text-gray-600 mb-2">Approval Trail</p>
            <div class="space-y-2">
              <div
                v-for="(approval, index) in detailsRequest.approvalPath"
                :key="index"
                class="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <p class="font-medium text-gray-900">{{ formatRole(approval.role) }}</p>
                    <p class="text-sm text-gray-600">{{ approval.approver ? approval.approver.name : 'N/A' }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <span class="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">{{ approval.status }}</span>
                  <p class="text-xs text-gray-500 mt-1">{{ formatDateTime(approval.timestamp) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Finance Review Comments -->
          <div v-if="detailsRequest.financeReview && detailsRequest.financeReview.comments" class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
            <p class="text-sm font-medium text-blue-700 mb-1">Finance Review</p>
            <p class="text-blue-800">{{ detailsRequest.financeReview.comments }}</p>
            <p class="text-xs text-blue-600 mt-2">By: {{ detailsRequest.financeReview.reviewedBy?.name || 'N/A' }} - {{ formatDateTime(detailsRequest.financeReview.reviewedAt) }}</p>
          </div>

          <!-- MD Review Comments -->
          <div v-if="detailsRequest.mdReview && detailsRequest.mdReview.comments" class="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r">
            <p class="text-sm font-medium text-purple-700 mb-1">Managing Director Review</p>
            <p class="text-purple-800">{{ detailsRequest.mdReview.comments }}</p>
            <p class="text-xs text-purple-600 mt-2">By: {{ detailsRequest.mdReview.reviewedBy?.name || 'N/A' }} - {{ formatDateTime(detailsRequest.mdReview.reviewedAt) }}</p>
          </div>

          <!-- Attachments -->
          <div v-if="hasAttachments(detailsRequest)">
            <p class="text-sm text-gray-600 mb-2">Attachments</p>
            <div class="flex flex-wrap gap-2">
              <button v-if="detailsRequest.attachments?.invoice"
                      @click="downloadAttachment(detailsRequest._id, 'invoice')"
                      class="text-sm bg-blue-100 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Invoice
              </button>
              <button v-if="detailsRequest.attachments?.contract"
                      @click="downloadAttachment(detailsRequest._id, 'contract')"
                      class="text-sm bg-green-100 text-green-700 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Contract
              </button>
              <button v-for="(doc, index) in detailsRequest.attachments?.otherDocuments || []"
                      :key="index"
                      @click="downloadAttachment(detailsRequest._id, `other-${index}`)"
                      class="text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
                </svg>
                Document {{ index + 1 }}
              </button>
            </div>
          </div>

          <!-- Close Button -->
          <button
            @click="closeDetailsModal"
            class="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Settlement Modal -->
    <div
      v-if="showSettlementModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeSettlementModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-lg w-full">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">Confirm Settlement</h2>

        <div v-if="selectedRequest" class="space-y-4">
          <!-- Request Summary -->
          <div class="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <p class="text-gray-600 text-sm">Request Amount</p>
            <p class="text-2xl font-bold text-gray-900">RF{{ selectedRequest.amount.toLocaleString() }}</p>
            <p class="text-gray-600 text-sm mt-2">{{ selectedRequest.requester ? selectedRequest.requester.name : 'N/A' }}</p>
          </div>

          <!-- Settlement Form -->
          <div>
            <label class="block text-gray-700 mb-2">Payment Reference *</label>
            <input
              v-model="settlementForm.paymentReference"
              type="text"
              placeholder="e.g., PAY-2024-12345"
              class="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label class="block text-gray-700 mb-2">Payment Date (Optional)</label>
            <input
              v-model="settlementForm.paymentDate"
              type="date"
              class="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3 mt-6">
            <button
              @click="confirmSettlement"
              :disabled="!settlementForm.paymentReference || processing"
              class="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {{ processing ? 'Processing...' : 'Confirm Settlement' }}
            </button>
            <button
              @click="closeSettlementModal"
              :disabled="processing"
              class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Settlement Modal -->
    <div
      v-if="showBulkSettlementModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="closeBulkSettlementModal"
    >
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">Bulk Settlement Confirmation</h2>

        <div class="space-y-4">
          <!-- Summary -->
          <div class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
            <p class="text-blue-700 font-medium">
              You are about to settle {{ selectedRequests.length }} requests
            </p>
            <p class="text-blue-600 text-sm mt-1">
              Total Amount: RF{{ getTotalAmount().toLocaleString() }}
            </p>
          </div>

          <!-- Selected Requests List -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-60 overflow-y-auto">
            <p class="text-gray-700 font-medium mb-3">Selected Requests:</p>
            <div class="space-y-2">
              <div
                v-for="request in getSelectedRequestsData()"
                :key="request._id"
                class="bg-white p-3 rounded-lg border border-gray-200 flex justify-between items-center"
              >
                <div>
                  <p class="font-medium text-gray-900">RF{{ request.amount.toLocaleString() }}</p>
                  <p class="text-sm text-gray-600">{{ request.requester ? request.requester.name : 'N/A' }}</p>
                </div>
                <button
                  @click="removeFromSelection(request._id)"
                  class="text-red-600 hover:text-red-700"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Settlement Form -->
          <div>
            <label class="block text-gray-700 mb-2">
              Payment Reference (Optional)
              <span class="text-gray-500 text-sm ml-2">Leave empty to auto-generate</span>
            </label>
            <input
              v-model="bulkSettlementForm.paymentReference"
              type="text"
              placeholder="e.g., BULK-2024-12345 (auto-generated if left empty)"
              class="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
            <p class="text-xs text-gray-500 mt-1">
              Each request will receive a unique reference based on this bulk reference
            </p>
          </div>

          <div>
            <label class="block text-gray-700 mb-2">Payment Date (Optional)</label>
            <input
              v-model="bulkSettlementForm.paymentDate"
              type="date"
              class="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3 mt-6">
            <button
              @click="confirmBulkSettlement"
              :disabled="processing"
              class="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              {{ processing ? 'Processing...' : `Settle ${selectedRequests.length} Requests` }}
            </button>
            <button
              @click="closeBulkSettlementModal"
              :disabled="processing"
              class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'Settlement',
  components: { Sidebar },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      pendingSettlementRequests: [],
      loading: false,
      showSettlementModal: false,
      showBulkSettlementModal: false,
      showDetailsModal: false,
      selectedRequest: null,
      detailsRequest: null,
      processing: false,
      selectedRequests: [], // Array of request IDs for bulk settlement
      settlementForm: {
        paymentReference: '',
        paymentDate: ''
      },
      bulkSettlementForm: {
        paymentReference: '',
        paymentDate: ''
      }
    };
  },
  mounted() {
    this.fetchPendingSettlements();
  },
  methods: {
    async fetchPendingSettlements() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/approved', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Filter for requests that are approved but not yet settled
        this.pendingSettlementRequests = response.data.filter(
          req => req.settlementStatus === 'pending_payment'
        );
      } catch (error) {
        console.error('Error fetching pending settlements:', error);
        alert('Failed to fetch pending settlements');
      } finally {
        this.loading = false;
      }
    },

    formatRole(role) {
      const roleMap = {
        'direct_manager': 'Direct Manager',
        'hod': 'Head of Department',
        'managing_director': 'Managing Director',
        'ceo': 'CEO'
      };
      return roleMap[role] || role;
    },

    openSettlementModal(request) {
      this.selectedRequest = request;
      this.settlementForm = {
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0] // Default to today
      };
      this.showSettlementModal = true;
    },

    closeSettlementModal() {
      if (!this.processing) {
        this.showSettlementModal = false;
        this.selectedRequest = null;
        this.settlementForm = {
          paymentReference: '',
          paymentDate: ''
        };
      }
    },

    async confirmSettlement() {
      if (!this.settlementForm.paymentReference) {
        alert('Payment reference is required');
        return;
      }

      this.processing = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/${this.selectedRequest._id}/settle`,
          this.settlementForm,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        alert(`Settlement successful! Payment Reference: ${this.settlementForm.paymentReference}`);
        this.closeSettlementModal();
        // Redirect to reports page after successful settlement
        this.$router.push({ name: 'Reports' });
      } catch (error) {
        console.error('Error processing settlement:', error);
        alert(error.response?.data?.message || 'Failed to process settlement');
      } finally {
        this.processing = false;
      }
    },

    viewRequestDetails(request) {
      this.detailsRequest = request;
      this.showDetailsModal = true;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.detailsRequest = null;
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      // Format date with time in CAT (Central Africa Time, GMT+2)
      return new Date(dateString).toLocaleString('en-RW', {
        timeZone: 'Africa/Kigali',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }) + ' CAT';
    },

    hasAttachments(request) {
      return request.attachments && (
        request.attachments.invoice ||
        request.attachments.contract ||
        (request.attachments.otherDocuments && request.attachments.otherDocuments.length > 0)
      );
    },

    async downloadAttachment(requestId, type) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/attachment/${requestId}/${type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}-${requestId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment');
      }
    },
    openCreateModal() {
      // Handle opening create modal
      console.log('Opening create modal');
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    },

    // Bulk settlement methods
    toggleSelection(requestId) {
      const index = this.selectedRequests.indexOf(requestId);
      if (index > -1) {
        this.selectedRequests.splice(index, 1);
      } else {
        this.selectedRequests.push(requestId);
      }
    },

    isSelected(requestId) {
      return this.selectedRequests.includes(requestId);
    },

    selectAll() {
      this.selectedRequests = this.pendingSettlementRequests.map(req => req._id);
    },

    clearSelection() {
      this.selectedRequests = [];
    },

    removeFromSelection(requestId) {
      const index = this.selectedRequests.indexOf(requestId);
      if (index > -1) {
        this.selectedRequests.splice(index, 1);
      }
      // Close modal if no more requests selected
      if (this.selectedRequests.length === 0) {
        this.closeBulkSettlementModal();
      }
    },

    getTotalAmount() {
      return this.pendingSettlementRequests
        .filter(req => this.selectedRequests.includes(req._id))
        .reduce((total, req) => total + req.amount, 0);
    },

    getSelectedRequestsData() {
      return this.pendingSettlementRequests
        .filter(req => this.selectedRequests.includes(req._id));
    },

    openBulkSettlementModal() {
      if (this.selectedRequests.length === 0) {
        alert('Please select at least one request to settle');
        return;
      }
      this.bulkSettlementForm = {
        paymentReference: '',
        paymentDate: new Date().toISOString().split('T')[0] // Default to today
      };
      this.showBulkSettlementModal = true;
    },

    closeBulkSettlementModal() {
      if (!this.processing) {
        this.showBulkSettlementModal = false;
        this.bulkSettlementForm = {
          paymentReference: '',
          paymentDate: ''
        };
      }
    },

    async confirmBulkSettlement() {
      if (this.selectedRequests.length === 0) {
        alert('No requests selected for settlement');
        return;
      }

      const confirmMessage = `Are you sure you want to settle ${this.selectedRequests.length} requests with a total amount of RF${this.getTotalAmount().toLocaleString()}?`;
      if (!confirm(confirmMessage)) {
        return;
      }

      this.processing = true;
      try {
        const token = localStorage.getItem('token');
        const payload = {
          requestIds: this.selectedRequests
        };

        // Only include paymentReference if user provided one
        if (this.bulkSettlementForm.paymentReference.trim()) {
          payload.paymentReference = this.bulkSettlementForm.paymentReference;
        }

        // Only include paymentDate if user provided one
        if (this.bulkSettlementForm.paymentDate) {
          payload.paymentDate = this.bulkSettlementForm.paymentDate;
        }

        const response = await axios.post(
          'https://guarantee.iwacufinance.com/cash-request/api/requests/bulk/settle',
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const { bulkReference, results } = response.data;

        // Display results
        let message = '';
        if (results.successful.length > 0) {
          message += `✓ Successfully settled ${results.successful.length} requests\n`;
          message += `Bulk Reference: ${bulkReference}\n\n`;
        }

        if (results.failed.length > 0) {
          message += `✗ Failed to settle ${results.failed.length} requests:\n`;
          results.failed.forEach(failure => {
            message += `  - Request ID: ${failure.requestId.slice(-8)}: ${failure.reason}\n`;
          });
        }

        alert(message);

        // Clear selection and close modal
        this.selectedRequests = [];
        this.closeBulkSettlementModal();

        // Refresh the list
        await this.fetchPendingSettlements();

      } catch (error) {
        console.error('Error processing bulk settlement:', error);
        alert(error.response?.data?.message || 'Failed to process bulk settlement');
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
</style>
